import { UserPatchRequestBody } from '@lio/api-types';
import { userQueryKey } from '@services/user/keys';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';
import { useStore } from '@store/GlobalStore';
import { TLayoutPreferences, TWalkthroughs } from '@store/types/UserStoreSlice.type';

type DisplayPreferencesPatch = Partial<
  Pick<TLayoutPreferences, 'clockFormat' | 'offeringGridColumns' | 'fontSize' | 'offeringColorMode'>
> & {
  walkthroughs?: TWalkthroughs;
};

export const useUpdateUser = ({ isEditingOwnProfile }: { isEditingOwnProfile: boolean }) => {
  const queryClient = useQueryClient();
  const client = useClient();
  const setLayoutPreferences = useStore((state) => state.setLayoutPreferences);
  const setWalkthroughs = useStore((state) => state.setWalkthroughs);
  const mutationFn = (params: {
    requestBody: UserPatchRequestBody;
    requestParams: {
      userId: string;
    };
  }) => {
    const { requestParams, requestBody } = params;
    const url = `user/${requestParams.userId}`;
    return client.patch(url, {
      json: requestBody,
    });
  };
  const result = useMutation({
    mutationFn,
    onSuccess: (data, request) => {
      queryClient.invalidateQueries({
        queryKey: userQueryKey.search,
      });
      queryClient.invalidateQueries({
        queryKey: userQueryKey.getWithParams(request.requestParams.userId),
      });

      if (isEditingOwnProfile) {
        queryClient.invalidateQueries({
          queryKey: userQueryKey.currentUser,
        });

        // Update layout preferences if displayPreferences were updated
        if (request.requestBody.displayPreferences) {
          const currentLayoutPreferences = useStore.getState().layoutPreferences;
          const displayPrefs = request.requestBody.displayPreferences as DisplayPreferencesPatch;
          const updatedPreferences: Partial<TLayoutPreferences> = {};

          if (displayPrefs.clockFormat !== undefined) {
            updatedPreferences.clockFormat = displayPrefs.clockFormat;
          }
          if (displayPrefs.offeringGridColumns !== undefined) {
            updatedPreferences.offeringGridColumns = displayPrefs.offeringGridColumns;
          }
          if (displayPrefs.fontSize !== undefined) {
            updatedPreferences.fontSize = displayPrefs.fontSize;
          }
          if (displayPrefs.offeringColorMode !== undefined) {
            updatedPreferences.offeringColorMode = displayPrefs.offeringColorMode;
          }

          if (Object.keys(updatedPreferences).length > 0) {
            setLayoutPreferences({
              ...currentLayoutPreferences,
              ...updatedPreferences,
            });
          }

          if (displayPrefs.walkthroughs !== undefined) {
            const currentWalkthroughs = useStore.getState().walkthroughs;
            setWalkthroughs({ ...currentWalkthroughs, ...displayPrefs.walkthroughs });
          }
        }
      }
    },
  });
  return { ...result, updateUser: result.mutateAsync };
};
